import { useState, useMemo, useEffect } from 'react'
import type { Json } from '../types/database'
import {
  calculateCanvasBounds,
  calculateCumulativeStartTimes,
  getStrokeTimingInfo,
  extractDrawingPages,
  calculateTimeRange,
  getGrayscaleColor,
  generateGrayscaleSVGContent,
  getStrokeEndTime,
  getDeletionTimeMap,
} from '../utils/drawingUtils'
import { Document, Page, pdfjs } from 'react-pdf'

// Set up PDF worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'

interface DrawingCanvasProps {
  drawingData: Json
  pdfUrl?: string | null
  isGrayscaleMode?: boolean
  setIsGrayscaleMode?: (value: boolean | ((prev: boolean) => boolean)) => void
  onCanvasReady?: () => void
}

export function DrawingCanvas({
  drawingData,
  pdfUrl,
  isGrayscaleMode = false,
  setIsGrayscaleMode,
  onCanvasReady
}: DrawingCanvasProps) {
  const [isAnimating, setIsAnimating] = useState(false)
  const [animationKey, setAnimationKey] = useState(0)
  const [sliderTime, setSliderTime] = useState<number>(0)
  const [pdfPageDimensions, setPdfPageDimensions] = useState<{ width: number; height: number } | null>(null)
  const [pdfRendered, setPdfRendered] = useState(false)

  const pages = extractDrawingPages(drawingData)

  // Notify parent when ready
  useEffect(() => {
    if (!onCanvasReady) return

    // If no PDF, we are ready quickly
    if (!pdfUrl) {
      const timer = setTimeout(() => onCanvasReady(), 500)
      return () => clearTimeout(timer)
    }

    // If PDF exists, we wait for actual rendering to finish
    if (pdfUrl && pdfRendered) {
      const timer = setTimeout(() => onCanvasReady(), 500) // Small buffer after render
      return () => clearTimeout(timer)
    }
  }, [pdfUrl, pdfRendered, onCanvasReady])

  if (pages.length === 0) {
    return (
      <div className="flex items-center justify-center p-8 bg-gray-50 rounded-lg">
        <p className="text-gray-500">페이지가 없습니다.</p>
      </div>
    )
  }

  const bounds = calculateCanvasBounds(pages)
  const { minX, minY, width, padding } = bounds

  // Add extra height for legend when in grayscale mode
  const legendExtraSpace = 70
  const height = bounds.height + (isGrayscaleMode ? legendExtraSpace : 0)

  // Calculate time range for grayscale mode and slider
  const timeRange = useMemo(() => calculateTimeRange(pages), [pages])

  // Slider range: 모든 stroke(삭제 포함) + 삭제 이벤트 기준
  const sliderRange = useMemo(() => {
    let minTime = Infinity
    let maxTime = -Infinity
    pages.forEach(page => {
      page.strokes.forEach(stroke => {
        if (stroke.points.length === 0) return
        const endTime = getStrokeEndTime(stroke)
        minTime = Math.min(minTime, endTime)
        maxTime = Math.max(maxTime, endTime)
      })
      page.deletions?.forEach(d => {
        const delTime = d.time_offset / 1_000_000
        maxTime = Math.max(maxTime, delTime)
      })
    })
    if (minTime === Infinity) return { min: 0, max: 0 }
    return { min: minTime, max: maxTime + 0.1 }
  }, [pages])

  // Initialize slider to max time (show all strokes initially)
  useEffect(() => {
    setSliderTime(sliderRange.max)
  }, [sliderRange.max])

  // Filter helper: determine if stroke should be shown based on slider time
  const shouldShowStroke = (stroke: any, strokeIndex: number, deletionTimeMap: Map<number, number>): boolean => {
    if (isAnimating) return true // Let CSS animation handle visibility
    const endTime = getStrokeEndTime(stroke)
    if (endTime > sliderTime) return false
    // 삭제된 stroke: 삭제 시점 이후면 안 보임
    const delTime = deletionTimeMap.get(strokeIndex)
    if (delTime !== undefined && sliderTime >= delTime) return false
    return true
  }

  const handleReplay = () => {
    setIsAnimating(true)
    setAnimationKey(prev => prev + 1)
    setSliderTime(sliderRange.max) // Reset slider to show all strokes

    // Calculate total animation duration (삭제 시점 포함)
    const totalDuration = sliderRange.max

    setTimeout(() => {
      setIsAnimating(false)
    }, (totalDuration + 0.5) * 1000) // 0.5s buffer for hide animation
  }

  const handleDownloadGrayscale = () => {
    // Use original bounds without extra height
    const originalBounds = calculateCanvasBounds(pages)
    const svgContent = generateGrayscaleSVGContent(pages, originalBounds)

    // Create blob and download
    const blob = new Blob([svgContent], { type: 'image/svg+xml' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `drawing-grayscale-${Date.now()}.svg`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <div className="flex justify-end">
          <div className="flex items-center gap-2">
            {setIsGrayscaleMode && (
              <button
                onClick={() => setIsGrayscaleMode(prev => !prev)}
                disabled={isAnimating}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isGrayscaleMode
                  ? 'text-white bg-gray-700 hover:bg-gray-800'
                  : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                  }`}
              >
                <span className="material-symbols-outlined text-base">
                  {isGrayscaleMode ? 'palette' : 'palette'}
                </span>
                <span>그레이스케일</span>
              </button>
            )}

            {isGrayscaleMode && (
              <button
                onClick={handleDownloadGrayscale}
                disabled={isAnimating}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="material-symbols-outlined text-base">download</span>
                <span>SVG 다운로드</span>
              </button>
            )}

            <button
              onClick={handleReplay}
              disabled={isAnimating}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="material-symbols-outlined text-base">replay</span>
              <span>애니메이션 재생</span>
            </button>
          </div>
        </div>

      </div>

      {/* Time slider control */}
      {sliderRange.max > sliderRange.min && (
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="flex items-center gap-3">
            <input
              type="range"
              min={sliderRange.min}
              max={sliderRange.max}
              step={0.01}
              value={sliderTime}
              onChange={(e) => setSliderTime(parseFloat(e.target.value))}
              disabled={isAnimating}
              aria-label="Drawing timeline position"
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <span className="text-sm text-gray-700 font-mono w-20 text-right">
              {sliderTime.toFixed(2)}s
            </span>
          </div>
        </div>
      )}

      {pages.map((page, pageIndex) => {
        const cumulativeStartTimes = calculateCumulativeStartTimes(page.strokes)
        const deletionTimeMap = getDeletionTimeMap(page)

        // Filter strokes based on slider time and deletion state
        const visibleStrokes = page.strokes
          .map((stroke, index) => ({ stroke, originalIndex: index }))
          .filter(({ stroke, originalIndex }) => shouldShowStroke(stroke, originalIndex, deletionTimeMap))

        const finalMinX = pdfUrl ? 0 : bounds.minX - padding
        const finalMinY = pdfUrl ? 0 : bounds.minY - padding

        // Default to calculated bounds width if no PDF
        let finalWidth = bounds.width
        let finalHeight = bounds.height

        if (pdfUrl && pdfPageDimensions) {
          // If PDF loaded, take the union of PDF size and Drawing Max Extent
          // Drawing Max Extent: bounds.maxX
          finalWidth = Math.max(pdfPageDimensions.width, bounds.maxX + padding)
          finalHeight = Math.max(pdfPageDimensions.height, bounds.maxY + padding)
        }

        return (
          <div key={pageIndex} className="bg-white rounded-lg border border-gray-200 p-4 overflow-auto">
            {pages.length > 1 && (
              <div className="mb-2 text-sm text-gray-600">
                페이지 {page.page_index + 1}
              </div>
            )}

            <div id={`drawing-canvas-${pageIndex}`} className="relative mx-auto" style={{
              width: pdfUrl ? finalWidth : width,
              height: pdfUrl ? finalHeight : height
            }}>
              {pdfUrl && (
                <div className="absolute top-0 left-0">
                  <Document file={pdfUrl} loading="PDF 로딩 중...">
                    <Page
                      pageNumber={page.page_index + 1}
                      // Render at original size (1:1 with points)
                      width={pdfPageDimensions?.width}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      onLoadSuccess={(page) => {
                        setPdfPageDimensions({ width: page.originalWidth, height: page.originalHeight })
                      }}
                      onRenderSuccess={() => setPdfRendered(true)}
                    />
                  </Document>
                </div>
              )}

              <svg
                key={`svg-${pageIndex}-${animationKey}`}
                width={finalWidth}
                height={finalHeight}
                viewBox={`${finalMinX} ${finalMinY} ${finalWidth} ${finalHeight}`}
                className={pdfUrl ? "absolute top-0 left-0 pointer-events-none" : "mx-auto border border-gray-100"}
                style={pdfUrl ? {
                  // Overlay style
                  width: finalWidth,
                  height: finalHeight
                } : { maxWidth: '100%', height: 'auto', backgroundColor: 'white' }}
              >

                {visibleStrokes.map(({ stroke, originalIndex }) => {
                  const cumulativeStartTime = cumulativeStartTimes[originalIndex]
                  const timingInfo = getStrokeTimingInfo(stroke, cumulativeStartTime)

                  // 삭제 애니메이션: 삭제 시점에 fade out
                  const delTime = deletionTimeMap.get(originalIndex)
                  const hasDeleteAnimation = isAnimating && delTime !== undefined

                  return (
                    <path
                      key={`${originalIndex}-${animationKey}`}
                      d={timingInfo.pathD}
                      stroke={isGrayscaleMode ? getGrayscaleColor(stroke, timeRange) : (stroke.color || '#000000')}
                      strokeWidth={stroke.width || 2}
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeDasharray={timingInfo.pathLength}
                      strokeDashoffset={timingInfo.pathLength}
                      style={{
                        animation: isAnimating
                          ? `drawStroke ${timingInfo.duration}s linear ${timingInfo.delay}s forwards${hasDeleteAnimation ? `, hideStroke 0.15s linear ${delTime}s forwards` : ''}`
                          : 'none',
                        strokeDashoffset: isAnimating ? timingInfo.pathLength : 0
                      }}
                    />
                  )
                })}

                {isGrayscaleMode && (() => {
                  const legendHeight = 40
                  const legendWidth = 420
                  const legendMargin = 15
                  const legendX = minX - padding + (width - legendWidth) / 2
                  const legendY = minY - padding + height - legendHeight - legendMargin

                  return (
                    <g>
                      {/* Legend background */}
                      <rect
                        x={legendX}
                        y={legendY}
                        width={legendWidth}
                        height={legendHeight}
                        fill="white"
                        stroke="#E5E7EB"
                        strokeWidth={1}
                        rx={4}
                        opacity={0.95}
                      />

                      {/* Legend title */}
                      <text
                        x={legendX + 10}
                        y={legendY + 22}
                        fontSize={12}
                        fill="#4B5563"
                        fontWeight="600"
                        fontFamily="system-ui, -apple-system, sans-serif"
                      >
                        시간 범위:
                      </text>

                      {/* Color swatches and labels */}
                      {['#CCCCCC', '#999999', '#666666', '#333333', '#000000'].map((color, idx) => {
                        const endPercent = (idx + 1) * 20
                        const endTime = timeRange.minTime + (timeRange.range * endPercent / 100)
                        const endTimeRounded = Math.ceil(endTime * 10) / 10
                        const xOffset = legendX + 75 + (idx * 67)

                        return (
                          <g key={idx}>
                            {/* Color swatch */}
                            <rect
                              x={xOffset}
                              y={legendY + 10}
                              width={16}
                              height={16}
                              fill={color}
                              stroke="#D1D5DB"
                              strokeWidth={1}
                              rx={2}
                            />

                            {/* Time label */}
                            <text
                              x={xOffset + 20}
                              y={legendY + 22}
                              fontSize={10}
                              fill="#374151"
                              fontFamily="monospace"
                            >
                              ~{endTimeRounded.toFixed(1)}s
                            </text>
                          </g>
                        )
                      })}
                    </g>
                  )
                })()}
              </svg>
            </div>
          </div>
        )
      })}
      <style>{`
        @keyframes drawStroke {
          to {
            stroke-dashoffset: 0;
          }
        }
        @keyframes hideStroke {
          to {
            opacity: 0;
          }
        }
      `}</style>
    </div>
  )
}
